import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Edit3, Download } from 'lucide-react';
import {
  Card,
  CardContent,
  CardTitle,
  CardDescription,
} from '@/components/ui-kit/card';

const steps = [
  {
    step: '1',
    titleKey: 'LANDING_HOW_STEP1_TITLE',
    descKey: 'LANDING_HOW_STEP1_DESC',
    icon: Search,
  },
  {
    step: '2',
    titleKey: 'LANDING_HOW_STEP2_TITLE',
    descKey: 'LANDING_HOW_STEP2_DESC',
    icon: Edit3,
  },
  {
    step: '3',
    titleKey: 'LANDING_HOW_STEP3_TITLE',
    descKey: 'LANDING_HOW_STEP3_DESC',
    icon: Download,
  },
];

export const HowItWorks = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="bg-white py-24 px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-20">
          <h2 className="text-3xl font-bold tracking-tight text-foreground font-aptos sm:text-4xl">
            {t('LANDING_HOW_IT_WORKS_TITLE')}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-open-sans">
            {t('LANDING_HOW_IT_WORKS_SUBTITLE')}
          </p>
        </div>

        <div
          ref={sectionRef}
          className={`relative grid grid-cols-1 gap-8 md:grid-cols-3${visible ? ' hiw-visible' : ''}`}
        >
          {/* Connecting line — draws left-to-right on reveal */}
          <div className="hiw-line absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-selise-blue/20 to-transparent hidden md:block" />

          {steps.map(({ step, titleKey, descKey, icon: Icon }) => (
            <div
              key={step}
              className="hiw-step relative flex flex-col items-center text-center"
            >
              {/* Icon circle */}
              <div className="hiw-icon relative z-10 flex h-24 w-24 items-center justify-center rounded-3xl bg-white border-4 border-gray-50 shadow-xl mb-8 transition-shadow duration-300 hover:shadow-2xl">
                <div className="absolute inset-0 bg-selise-blue/5 rounded-3xl" />
                <Icon className="h-10 w-10 text-selise-blue" />
              </div>

              {/* Step card */}
              <Card className="px-6 py-4 border-none bg-white shadow-xl transition-transform duration-300 hover:-translate-y-1">
                <CardContent className="space-y-4">
                  <span className="text-sm font-bold text-selise-blue font-aptos tracking-widest">
                    {t('LANDING_HOW_STEP_BADGE')} {step}
                  </span>
                  <CardTitle className="text-xl font-bold text-foreground font-aptos">
                    {t(titleKey)}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground leading-relaxed font-open-sans">
                    {t(descKey)}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
